import type { DocumentItem } from './model.js';
import { extensionFromName } from './mapper.js';

/**
 * A file-type descriptor. Note `iconKey` is a *string*, never a component —
 * that is what keeps core free of any renderer dependency. Each framework
 * package maps the key to its own icon.
 */
export interface FileTypeDescriptor {
  /** Human label shown in a "Type" column, e.g. `"PDF"`. */
  label: string;
  /** Stable key a renderer maps to an icon, e.g. `"pdf"`. */
  iconKey: string;
  /** A CSS colour, ideally a `var(--de-*)` token. */
  color?: string;
  extensions?: string[];
  mimeTypes?: string[];
  /** Hint for consumers that build a preview pane. */
  previewable?: boolean;
}

const byExtension = new Map<string, FileTypeDescriptor>();
const byMimeType = new Map<string, FileTypeDescriptor>();

export const FOLDER_TYPE: FileTypeDescriptor = {
  label: 'Folder',
  iconKey: 'folder',
  color: 'var(--de-type-folder)',
};

export const UNKNOWN_TYPE: FileTypeDescriptor = {
  label: 'File',
  iconKey: 'file',
  color: 'var(--de-type-file)',
};

/**
 * Register a file type. Consumers extend the built-ins the same way:
 * `registerFileType({ extensions: ['rs'], label: 'Rust', iconKey: 'rust' })`.
 */
export function registerFileType(descriptor: FileTypeDescriptor): void {
  for (const ext of descriptor.extensions ?? []) {
    byExtension.set(ext.replace(/^\./, '').toLowerCase(), descriptor);
  }
  for (const mime of descriptor.mimeTypes ?? []) {
    byMimeType.set(mime.toLowerCase(), descriptor);
  }
}

/** Resolution order: explicit mimeType -> extension -> name suffix -> fallback. */
export function resolveFileType(item: Pick<DocumentItem, 'type' | 'name' | 'mimeType' | 'extension'>): FileTypeDescriptor {
  if (item.type === 'folder') return FOLDER_TYPE;

  if (item.mimeType) {
    const exact = byMimeType.get(item.mimeType.toLowerCase());
    if (exact) return exact;
  }
  const ext = item.extension ?? extensionFromName(item.name ?? '');
  if (ext) {
    const hit = byExtension.get(ext.replace(/^\./, '').toLowerCase());
    if (hit) return hit;
    // Unknown but present extension: show it rather than a generic "File".
    return { ...UNKNOWN_TYPE, label: ext.toUpperCase() };
  }
  return UNKNOWN_TYPE;
}

/** Remove every registration, including built-ins. Mainly for tests. */
export function clearFileTypes(): void {
  byExtension.clear();
  byMimeType.clear();
}

const BUILT_INS: FileTypeDescriptor[] = [
  { label: 'PDF', iconKey: 'pdf', color: 'var(--de-type-pdf)', extensions: ['pdf'], mimeTypes: ['application/pdf'], previewable: true },
  { label: 'Word', iconKey: 'doc', color: 'var(--de-type-doc)', extensions: ['doc', 'docx', 'rtf', 'odt'], mimeTypes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
  { label: 'Excel', iconKey: 'sheet', color: 'var(--de-type-sheet)', extensions: ['xls', 'xlsx', 'csv', 'tsv', 'ods'], mimeTypes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'] },
  { label: 'PowerPoint', iconKey: 'slides', color: 'var(--de-type-slides)', extensions: ['ppt', 'pptx', 'odp'], mimeTypes: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'] },
  { label: 'Image', iconKey: 'image', color: 'var(--de-type-image)', extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg', 'bmp', 'ico', 'heic'], mimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'], previewable: true },
  { label: 'Video', iconKey: 'video', color: 'var(--de-type-video)', extensions: ['mp4', 'mov', 'webm', 'avi', 'mkv'], mimeTypes: ['video/mp4', 'video/webm', 'video/quicktime'], previewable: true },
  { label: 'Audio', iconKey: 'audio', color: 'var(--de-type-audio)', extensions: ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'], mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/ogg'] },
  { label: 'Archive', iconKey: 'archive', color: 'var(--de-type-archive)', extensions: ['zip', 'tar', 'gz', 'tgz', 'rar', '7z', 'bz2'], mimeTypes: ['application/zip', 'application/gzip', 'application/x-tar'] },
  { label: 'Markdown', iconKey: 'markdown', color: 'var(--de-type-text)', extensions: ['md', 'mdx', 'markdown'], mimeTypes: ['text/markdown'], previewable: true },
  { label: 'Text', iconKey: 'text', color: 'var(--de-type-text)', extensions: ['txt', 'log'], mimeTypes: ['text/plain'], previewable: true },
  { label: 'Code', iconKey: 'code', color: 'var(--de-type-code)', extensions: ['ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs', 'py', 'go', 'rs', 'java', 'rb', 'php', 'c', 'h', 'cpp', 'cs', 'swift', 'kt', 'sh', 'sql'], previewable: true },
  { label: 'Data', iconKey: 'data', color: 'var(--de-type-code)', extensions: ['json', 'yaml', 'yml', 'toml', 'xml'], mimeTypes: ['application/json', 'application/xml'], previewable: true },
];

/** (Re)install the built-in registrations. Called once at module load. */
export function registerBuiltInFileTypes(): void {
  for (const descriptor of BUILT_INS) registerFileType(descriptor);
}

registerBuiltInFileTypes();
